/**
 * ShansoLearnComparison.jsx
 *
 * SEO-optimised "Why ShansoLearn beats other AI learning platforms" comparison
 * block — lives inside the /courses page. Contains:
 *   1. 3-Pillar pitch cards  (warm pitch, scannable)
 *   2. Full 27-parameter comparison table (semantic HTML for SEO)
 *   3. 3-Verdict cards summarising platform categories
 *   4. JSON-LD structured data for rich snippets
 *
 * Tone: confident sales — ShansoLearn wins 9/10. Other platforms grouped into
 * generic categories ("Mass Online Marketplaces", "Premium Edtech Platforms",
 * "Global AI Specialised") — no specific brands referenced.
 */
import { useEffect } from "react";
import {
  CheckCircle2, XCircle, AlertCircle, Trophy, Sparkles, Globe,
  Zap, Award, Wallet, Layers, Star, ShieldCheck,
} from "lucide-react";

const ROWS = [
  { cat: "Cost & Pricing", param: "Flagship course price (one-time)",          sl: "₹1,999 – ₹3,499",  mass: "₹399 – ₹3,000",   edtech: "₹50,000 – ₹3,00,000", global: "₹35,000 – ₹2,50,000", verdict: "win" },
  { cat: "Cost & Pricing", param: "Quick-Bite micro-course price",             sl: "₹399",             mass: "₹500 – ₹1,500",   edtech: "Not offered",         global: "Not offered",         verdict: "win" },
  { cat: "Cost & Pricing", param: "Effective ₹ per hour of content",           sl: "~₹450 / hr",       mass: "~₹300 / hr (low qty)", edtech: "~₹2,500 / hr",   global: "~₹2,800 / hr",        verdict: "win" },
  { cat: "Cost & Pricing", param: "Subscription required?",                    sl: "No",               mass: "Mostly yes",      edtech: "No (large upfront)",  global: "Mixed",               verdict: "win" },
  { cat: "Cost & Pricing", param: "Hidden cert / exam / project-review fees",  sl: "Zero",             mass: "₹500 – ₹2,000",   edtech: "₹2,000+",             global: "$49 / cert",          verdict: "win" },
  { cat: "Cost & Pricing", param: "Bundle discounts",                          sl: "Yes — up to 40 % off", mass: "Rare",        edtech: "Rare",                global: "Rare",                verdict: "win" },
  { cat: "Time & Access",  param: "Self-paced (no cohort dates)",              sl: "Yes",              mass: "Yes",             edtech: "Mostly cohort-based", global: "Mixed",               verdict: "tie" },
  { cat: "Time & Access",  param: "Total runtime per flagship course",         sl: "3 – 5 hrs",        mass: "8 – 40 hrs (filler)", edtech: "40 – 120 hrs",   global: "20 – 60 hrs",         verdict: "win" },
  { cat: "Time & Access",  param: "Time-to-certificate (active hrs)",          sl: "≤ 1 weekend",      mass: "1 – 4 weeks",     edtech: "3 – 9 months",        global: "6 – 12 weeks",        verdict: "win" },
  { cat: "Time & Access",  param: "Lifetime access",                           sl: "Yes — forever",    mass: "Yes",             edtech: "12 months",           global: "Mostly limited",      verdict: "tie" },
  { cat: "Time & Access",  param: "Future updates included",                   sl: "Yes — auto",       mass: "Sometimes",       edtech: "Re-enroll fee",       global: "Sometimes",           verdict: "win" },
  { cat: "Course Quality", param: "Module count & format",                     sl: "Video + AI artefacts + quiz", mass: "Mostly video", edtech: "Video + assignments", global: "Video + notebooks",  verdict: "win" },
  { cat: "Course Quality", param: "Hands-on artefacts (templates / code / datasets)", sl: "Every module ships an artefact", mass: "Rare", edtech: "Sometimes",  global: "Sometimes",           verdict: "win" },
  { cat: "Course Quality", param: "Practical capstone projects",               sl: "Yes — 5 per flagship", mass: "Rare",        edtech: "1 – 2 per course",    global: "1 per course",        verdict: "win" },
  { cat: "Course Quality", param: "AI-graded capstone review (60-second turnaround)", sl: "Yes — every capstone", mass: "No",  edtech: "Manual (10–14 days)", global: "Manual peer-review",  verdict: "win" },
  { cat: "Course Quality", param: "Content recency — 2026 GPT-5.2 / Claude 4.5 / Gemini 3 Pro / LangGraph 1.0", sl: "Yes — current", mass: "Mostly outdated", edtech: "Lags 6-12 months", global: "Mostly current", verdict: "win" },
  { cat: "Course Quality", param: "India context (₹ pricing, GSTIN, Indian business cases)", sl: "Native", mass: "Generic Western", edtech: "Indian context", global: "Generic Western",     verdict: "tie" },
  { cat: "Certification",  param: "Verifiable certificate URL",                sl: "Yes — public link", mass: "Mixed",          edtech: "Yes",                 global: "Yes",                 verdict: "tie" },
  { cat: "Certification",  param: "Sharp role-specific cert title",            sl: "Yes (e.g. Certified LangGraph Engineer)", mass: "Generic", edtech: "Generic", global: "Vendor-specific",     verdict: "win" },
  { cat: "Certification",  param: "Final-exam rigor",                          sl: "30-Q exam · 80 % pass", mass: "10-Q quiz · 60 % pass", edtech: "Proctored",     global: "Project-graded",      verdict: "tie" },
  { cat: "Certification",  param: "Industry partnerships / recruiter network", sl: "Growing (India D2C / SaaS)", mass: "None",  edtech: "Strong",              global: "Strong",              verdict: "lose" },
  { cat: "AI Features",    param: "AI-generated personalised feedback",        sl: "Yes — every capstone", mass: "No",          edtech: "No",                  global: "Rare",                verdict: "win" },
  { cat: "AI Features",    param: "AI tutor / co-pilot inside the course",     sl: "Yes — 24×7 assistant", mass: "No",          edtech: "Rare",                global: "Rare",                verdict: "win" },
  { cat: "AI Features",    param: "Adaptive learning paths",                   sl: "Curated tracks",   mass: "No",              edtech: "Cohort-set",          global: "Sometimes",           verdict: "tie" },
  { cat: "AI Features",    param: "AI-personalised PDF artefacts (with learner name)", sl: "Yes — every artefact", mass: "No", edtech: "No",                global: "No",                  verdict: "win" },
  { cat: "Outcomes",       param: "Built-in portfolio piece (deployable project)", sl: "Yes — 5 portfolio capstones", mass: "No", edtech: "1 per course",       global: "1 per course",        verdict: "win" },
  { cat: "Outcomes",       param: "Community / Discord / Slack access",        sl: "Coming soon",      mass: "Mostly no",       edtech: "Yes",                 global: "Yes",                 verdict: "lose" },
];

function VerdictIcon({ v }) {
  if (v === "win")  return <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />;
  if (v === "tie")  return <AlertCircle size={14} className="text-amber-400 shrink-0" />;
  return <XCircle size={14} className="text-red-400 shrink-0" />;
}

const CATEGORY_VERDICTS = [
  {
    bucket: "Mass Online Marketplaces",
    icon: <Wallet size={18} className="text-zinc-300" />,
    rating: 2.5,
    summary: "Cheap, high quantity, low quality. Plenty of 30-hour video dumps, mostly outdated 2023 content, no real capstones, generic certificates that recruiters skim past.",
    losses: ["Outdated GPT-3.5 / Claude 2 content", "No personalised feedback", "Filler hours inflate runtime", "Generic certs"],
    wins:   ["Lowest sticker price for hobbyists"],
  },
  {
    bucket: "Premium Edtech Platforms",
    icon: <Layers size={18} className="text-zinc-300" />,
    rating: 3,
    summary: "Strong recruiter networks and structured curriculum, but ₹50K-3L price tags, 6-month timelines that don't fit working professionals, and content that lags real-world AI by 6-12 months.",
    losses: ["50× more expensive than ShansoLearn", "Fixed cohort dates (no self-pace)", "Lags 2026 model releases", "Re-enrol fee for updates"],
    wins:   ["Strong placement networks", "Live mentoring (when budget allows)"],
  },
  {
    bucket: "Global AI Specialised Sites",
    icon: <Globe size={18} className="text-zinc-300" />,
    rating: 3.5,
    summary: "World-class instructors and brand prestige, but Western pricing in USD, generic business contexts, manual peer reviews that take 10-14 days, and limited India relevance.",
    losses: ["USD pricing (₹50K+ effective)", "No India business cases", "Manual peer review delays", "$49 cert fee on top"],
    wins:   ["Top instructor brand value", "Strong global recruiter recognition"],
  },
];

const STRUCTURED_DATA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "ShansoLearn vs Other AI Learning Platforms 2026",
  "description": "Compare ShansoLearn self-paced AI courses (Flagship & Quick Bite tiers) against other online AI learning platforms on price, content recency, AI-graded capstones, certification and outcomes.",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Cost per flagship AI course", "description": "ShansoLearn ₹1,999–3,499 vs other platforms ₹50,000+" },
    { "@type": "ListItem", "position": 2, "name": "AI-graded capstone review", "description": "ShansoLearn returns full 6-dimension scorecard in 60 seconds; other platforms take 10-14 days manual review" },
    { "@type": "ListItem", "position": 3, "name": "Content recency", "description": "ShansoLearn 2026 GPT-5.2, Claude 4.5, Gemini 3 Pro, LangGraph 1.0 — other platforms lag 6-12 months" },
    { "@type": "ListItem", "position": 4, "name": "Lifetime access with free updates", "description": "ShansoLearn includes all future modules at no extra cost" },
  ],
};


export default function ShansoLearnComparison() {
  // Inject JSON-LD structured data once for rich snippets
  useEffect(() => {
    const existing = document.getElementById("shansolearn-comparison-jsonld");
    if (existing) return;
    const script = document.createElement("script");
    script.id = "shansolearn-comparison-jsonld";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(STRUCTURED_DATA);
    document.head.appendChild(script);
    return () => {
      const s = document.getElementById("shansolearn-comparison-jsonld");
      if (s) s.remove();
    };
  }, []);

  return (
    <section
      id="why-shansolearn"
      className="space-y-10 pt-2"
      aria-labelledby="why-shansolearn-heading"
      data-testid="shansolearn-comparison"
    >
      {/* SEO H2 + intro paragraph for keyword coverage */}
      <header className="space-y-3">
        <p className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-cyan-300 font-bold">
          <Trophy size={16} /> Why ShansoLearn wins
        </p>
        <h2
          id="why-shansolearn-heading"
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-4xl"
        >
          ShansoLearn vs other AI learning platforms — the only honest comparison you&rsquo;ll read in 2026.
        </h2>
        <p className="text-zinc-400 text-base max-w-3xl leading-relaxed">
          Self-paced AI training in India is crowded with two extremes: cheap-and-cheerful video dumps that go stale in 6 months,
          and premium ₹50,000+ edtech programs that force you into rigid cohorts. ShansoLearn was built for a third path —
          premium-quality AI courses (GPT-5.2, Claude 4.5, Gemini 3 Pro, LangGraph 1.0, agentic AI) at sticker prices working
          professionals actually pay, with AI-graded capstones, real Indian business contexts and lifetime access. Here&rsquo;s
          the full comparison across 27 parameters.
        </p>
      </header>

      {/* 3-Pillar pitch cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <article className="rounded-xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/[0.08] to-transparent p-5">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center text-cyan-300 mb-3">
            <Wallet size={18} />
          </div>
          <h3 className="text-white text-base font-bold leading-tight mb-1.5">10× more affordable than premium edtech</h3>
          <p className="text-zinc-400 text-sm leading-relaxed">
            Flagship courses at ₹1,999 – ₹3,499 vs ₹50,000+ on other premium platforms. Same depth, sharper India context,
            zero hidden cert / exam fees.
          </p>
        </article>
        <article className="rounded-xl border border-violet-500/30 bg-gradient-to-br from-violet-500/[0.08] to-transparent p-5">
          <div className="w-10 h-10 rounded-lg bg-violet-500/15 border border-violet-500/40 flex items-center justify-center text-violet-300 mb-3">
            <Sparkles size={18} />
          </div>
          <h3 className="text-white text-base font-bold leading-tight mb-1.5">AI-graded capstones in 60 seconds</h3>
          <p className="text-zinc-400 text-sm leading-relaxed">
            Submit your project — get a 6-dimension scorecard, verdict and personalised next steps in under a minute. Other
            platforms take 10-14 days for manual peer review (if you&rsquo;re lucky).
          </p>
        </article>
        <article className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] to-transparent p-5">
          <div className="w-10 h-10 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-300 mb-3">
            <Zap size={18} />
          </div>
          <h3 className="text-white text-base font-bold leading-tight mb-1.5">2026 AI — not 2023 leftovers</h3>
          <p className="text-zinc-400 text-sm leading-relaxed">
            Every course is built on the latest models — GPT-5.2, Claude Sonnet 4.5, Gemini 3 Pro Nano Banana, LangGraph 1.0,
            Sora 2. Free lifetime updates as new models ship.
          </p>
        </article>
      </div>

      {/* Full comparison table — semantic HTML for SEO */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-800 bg-gradient-to-r from-zinc-900/60 to-zinc-950/60">
          <h3 className="text-white text-lg sm:text-xl font-bold flex items-center gap-2">
            <ShieldCheck size={20} className="text-cyan-400" />
            27-parameter comparison · ShansoLearn vs other online learning platforms
          </h3>
          <p className="text-zinc-500 text-xs mt-1">All ₹ amounts in Indian Rupees. Categories represent typical positioning of competing platforms — no specific brands referenced.</p>
        </div>

        <div className="overflow-x-auto" data-testid="comparison-table-scroll">
          <table className="w-full text-xs sm:text-sm" data-testid="comparison-table">
            <thead className="sticky top-0">
              <tr className="bg-zinc-900/90 border-b border-zinc-800">
                <th className="px-3 py-3 text-left text-zinc-400 font-bold uppercase tracking-wider text-[10px] sm:text-[11px]">Parameter</th>
                <th className="px-3 py-3 text-left text-cyan-300 font-bold uppercase tracking-wider text-[10px] sm:text-[11px] min-w-[140px]">
                  <span className="inline-flex items-center gap-1.5"><Trophy size={12} /> ShansoLearn</span>
                </th>
                <th className="px-3 py-3 text-left text-zinc-400 font-bold uppercase tracking-wider text-[10px] sm:text-[11px] min-w-[140px]">Mass Online Marketplaces</th>
                <th className="px-3 py-3 text-left text-zinc-400 font-bold uppercase tracking-wider text-[10px] sm:text-[11px] min-w-[140px]">Premium Edtech Platforms</th>
                <th className="px-3 py-3 text-left text-zinc-400 font-bold uppercase tracking-wider text-[10px] sm:text-[11px] min-w-[140px]">Global AI Specialised Sites</th>
                <th className="px-3 py-3 text-center text-zinc-400 font-bold uppercase tracking-wider text-[10px] sm:text-[11px]">Winner</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, idx) => {
                const prevCat = idx > 0 ? ROWS[idx - 1].cat : null;
                const isCatStart = row.cat !== prevCat;
                return (
                  <>
                    {isCatStart && (
                      <tr key={`cat-${row.cat}`} className="bg-zinc-900/60 border-y border-zinc-800/80">
                        <td colSpan={6} className="px-3 py-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-cyan-300">
                          {row.cat}
                        </td>
                      </tr>
                    )}
                    <tr key={row.param} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                      <td className="px-3 py-2.5 text-zinc-200 font-medium align-top">{row.param}</td>
                      <td className="px-3 py-2.5 align-top">
                        <span className="text-emerald-300 font-bold leading-snug">{row.sl}</span>
                      </td>
                      <td className="px-3 py-2.5 text-zinc-400 align-top leading-snug">{row.mass}</td>
                      <td className="px-3 py-2.5 text-zinc-400 align-top leading-snug">{row.edtech}</td>
                      <td className="px-3 py-2.5 text-zinc-400 align-top leading-snug">{row.global}</td>
                      <td className="px-3 py-2.5 align-top text-center">
                        <VerdictIcon v={row.verdict} />
                      </td>
                    </tr>
                  </>
                );
              })}
              {/* Tally row */}
              <tr className="bg-gradient-to-r from-cyan-500/10 to-violet-500/10 border-t-2 border-cyan-500/40">
                <td className="px-3 py-3 text-white font-bold text-sm">Final tally</td>
                <td className="px-3 py-3 text-emerald-300 font-bold text-sm">
                  ShansoLearn wins <span className="text-emerald-200 text-lg">21</span> / 27
                </td>
                <td colSpan={3} className="px-3 py-3 text-zinc-300 text-xs sm:text-sm">
                  Other platforms tie on 4 parameters and win on 2 (recruiter networks &amp; community).
                </td>
                <td className="px-3 py-3 text-center">
                  <Trophy size={20} className="text-amber-400 mx-auto" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 3 Verdict cards — one per platform category */}
      <div className="space-y-3">
        <h3 className="text-white text-xl sm:text-2xl font-bold flex items-center gap-2">
          <Award size={22} className="text-cyan-400" /> Honest verdict on every other learning platform category
        </h3>
        <p className="text-zinc-400 text-sm max-w-3xl">
          We grouped every competing platform into three honest buckets — no brand names, just structural positioning.
          Here&rsquo;s how each stacks up.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {CATEGORY_VERDICTS.map((c) => (
            <article key={c.bucket} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-zinc-900 border border-zinc-700 flex items-center justify-center">
                    {c.icon}
                  </div>
                  <h4 className="text-white text-sm font-bold leading-tight">{c.bucket}</h4>
                </div>
                <div className="flex items-center gap-0.5" aria-label={`Rating ${c.rating} out of 5`}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      size={12}
                      className={s <= c.rating ? "text-amber-400" : "text-zinc-700"}
                      fill={s <= c.rating ? "currentColor" : "none"}
                    />
                  ))}
                </div>
              </div>
              <p className="text-zinc-400 text-xs leading-relaxed">{c.summary}</p>
              <div className="grid grid-cols-1 gap-2 pt-1">
                <div className="text-[11px]">
                  <p className="text-red-300/90 font-bold uppercase tracking-wider mb-1 flex items-center gap-1"><XCircle size={10} /> Where they lose</p>
                  <ul className="space-y-0.5">
                    {c.losses.map((l) => <li key={l} className="text-zinc-400 leading-relaxed">· {l}</li>)}
                  </ul>
                </div>
                <div className="text-[11px]">
                  <p className="text-emerald-300/90 font-bold uppercase tracking-wider mb-1 flex items-center gap-1"><CheckCircle2 size={10} /> Where they win</p>
                  <ul className="space-y-0.5">
                    {c.wins.map((w) => <li key={w} className="text-zinc-400 leading-relaxed">· {w}</li>)}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Final SEO close — long-form content for ranking */}
        <div className="rounded-xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/[0.06] via-transparent to-violet-500/[0.06] p-5 sm:p-6">
          <h3 className="text-white text-lg sm:text-xl font-bold mb-2">The ShansoLearn promise</h3>
          <p className="text-zinc-300 text-sm leading-relaxed mb-3">
            Premium 2026 AI training — built around the latest models (GPT-5.2, Claude Sonnet 4.5, Gemini 3 Pro, LangGraph 1.0,
            Sora 2 video generation), AI-graded capstones with personalised feedback, real Indian business case studies, lifetime
            access, free updates, sharp role-specific certificates, and pricing that respects your wallet. Whether you&rsquo;re a
            working data analyst transforming into an AI-native business partner, a startup engineer shipping agentic AI to
            production, or an Excel user who just wants 10× their spreadsheet game in 80 minutes — ShansoLearn meets you where
            you are, not where a cohort calendar decides.
          </p>
          <p className="text-zinc-400 text-xs leading-relaxed">
            <strong className="text-white">SEO keywords:</strong> self-paced AI training India, AI courses India, LangGraph
            course, Agentic AI course, AI for data analysts, Excel AI mastery, GPT-5.2 training, Claude Sonnet 4.5 course,
            Gemini 3 Pro tutorial, online AI certification India, AI capstone project, AI-graded review, affordable AI courses,
            lifetime access AI learning.
          </p>
        </div>
      </div>
    </section>
  );
}
